import React, { useEffect, useMemo, useState } from 'react'

import { Box, Stack, Typography, alpha, useTheme } from '@mui/material'
import { Card, Skeleton, Tabs } from '@repo/ui'

import { SelectField } from 'components'
import { api } from 'services'

/**
 * ─────────────────────────────────────────────────────────────────────────────
 * Email structure — how a Vera email is built, as a reference surface.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * This started life as a rendered specimen file in a scratchpad directory. That
 * is the wrong home for it twice over: a scratchpad is per-session and
 * untracked, so the specimen died with the session that made it, and it was a
 * SNAPSHOT — a frozen copy of the dark-mode block that would keep asserting the
 * old values for as long as anyone kept reading it. §64 records what happens
 * when a document about the email system decays quietly: that section cited a
 * guard for five days after the guard was deleted, and its own closing
 * instruction is the one this file is built around — resolve a load-bearing fact
 * against the tree at the moment you need it, never against a sentence written
 * earlier.
 *
 * So the class contract here is NOT typed out. It is parsed, live, out of the
 * bytes the API renders.
 *
 * ## Why the RENDERED BYTES and not `base-email.tsx`
 *
 * The obvious source is the `@media (prefers-color-scheme: dark)` block in
 * `packages/emails/src/components/base-email.tsx`. Two reasons this reads the
 * render instead:
 *
 *  1. **`apps/platform` does not depend on `@repo/emails`** — checked, and
 *     `EmailPreview.tsx` / `LogoUploadCard.tsx` both already carry the same
 *     note. There is no import to make. A `?raw` import across the workspace by
 *     relative path would work in Vite and would be a build-time dependency
 *     nothing declares, which is a worse trade than an HTTP call.
 *  2. **The source cannot show the values.** Every colour in that block is a
 *     template interpolation — `${enforcedDarkTokens.textColor}`,
 *     `${readableLabel(...).color}`, `${PILL_DARK.warning.fill}`. Reading the
 *     `.tsx` gets you the shape of the contract and none of its content. The
 *     render has both, already resolved through the same contrast enforcement a
 *     real send applies.
 *
 * The endpoint is `GET /internal/labs/emails/:id/preview`, the one the
 * Notifications lab uses, so this surface and that one cannot disagree about
 * what an email is. `mode=dark` is required rather than incidental:
 * `LabsController.forceColorScheme` UNWRAPS the media query on dark (rules kept,
 * condition dropped) and DELETES the whole block on light. So dark is the only
 * mode that carries the contract at all — see that method's docblock for why the
 * asymmetry exists, and note the parser below handles the wrapped shape too, so
 * a future change to that method degrades to "still works" rather than "renders
 * an empty table".
 *
 * ## What is derived and what is prose, stated plainly
 *
 * | Shown | Source |
 * |---|---|
 * | class names, their selectors, their dark declarations | parsed, CSSOM |
 * | the light value each class overrides | parsed, inline styles on the render |
 * | which classes this render actually USES | parsed, `[class]` walk |
 * | the contrast meta | parsed, `<meta name="x-vera-contrast">` |
 * | "what it is for" | prose, `CLASS_NOTES` below — PARTIAL by design |
 * | the structural rules and the traps | prose |
 *
 * `CLASS_NOTES` being partial is the load-bearing part. A class added to the
 * dark block tomorrow renders here on its own, in the table, flagged as
 * undocumented — rather than being silently dropped because this file had not
 * heard of it. A lookup keyed on a hand-written list would have exactly the
 * failure mode the whole surface exists to avoid.
 */

// ── Types ────────────────────────────────────────────────────────────────────

interface EmailTemplateSummary {
  id: string
  name: string
  group: 'app' | 'platform' | 'shared'
  category: string
  status: 'live' | 'draft'
}

interface DarkDeclaration {
  prop: string
  value: string
  important: boolean
}

interface DarkRule {
  selectorText: string
  /** Every `v-*` class named anywhere in the selector, deduped, in source order. */
  classNames: string[]
  decls: DarkDeclaration[]
}

interface ParsedRender {
  rules: DarkRule[]
  /** Classes the dark block defines. */
  definedClasses: string[]
  /** `v-*` classes actually present on elements in the light render. */
  usedClasses: string[]
  /** `<meta name="x-vera-contrast">`, or null when no colour was adjusted. */
  contrastMeta: string | null
  /** Light inline value for a (class, property) pair, where the render exercises it. */
  lightValue: (className: string, prop: string) => string | null
}

// ── Prose: deliberately PARTIAL, see the module docblock ─────────────────────

/**
 * What each class is FOR. Keyed by class name; a class missing from here still
 * renders, marked. Never turn this into the source of the class list.
 */
const CLASS_NOTES: Record<string, string> = {
  'v-bg':
    'The page background behind the card. Painted on a Section we own, never on <Body> — react-email puts Body’s style on a generated <td> that keeps no class, and most clients inline the body into their own document, so a class on <body> never arrives. That is how a dark card ended up framed by a white band.',
  'v-card': 'The card surface the message sits on.',
  'v-text':
    'Body text, and body links. The `:not(.v-btn)` exclusion is not cosmetic — see the note beside this row.',
  'v-muted': 'Secondary text: footers, captions, the “Powered by” lockup, sub-labels.',
  'v-border': 'Anything whose BORDER carries a token colour — card edges, dividers drawn as borders.',
  'v-border-fill':
    'A divider drawn as a FILLED element rather than a border. Same token, different property, so it needs its own class: one rule cannot repaint both `border-color` and `background-color` without repainting things that wanted only one.',
  'v-btn': 'The primary button. Sets fill AND ink — see the note beside this row.',
  'v-avatar':
    'The initials plate behind an actor’s monogram. A fixed neutral rather than a token, which is why it needs a class of its own: it is the one surface here whose dark counterpart cannot be derived from the dark token set. Carries ONLY this class, never `v-muted` as well.',
  'v-pill-neutral': 'Status pill — neutral tone. Fill, ink and border.',
  'v-pill-warning': 'Status pill — warning tone. Fill, ink and border.',
  'v-pill-danger': 'Status pill — danger tone. Fill, ink and border.',
  'v-pill-success': 'Status pill — success tone. Fill, ink and border.',
  'v-logo-light':
    'The light (black) wordmark. Both marks ship on every send and the CLIENT picks, because a server render cannot observe the recipient’s device theme.',
  'v-logo-dark': 'The dark (white) wordmark. Swapped in with `display: block` — it owns its own line.',
  'v-glyph-light':
    'The light half of an icon-badge glyph pair. Same swap mechanism as the wordmark but `inline-block`, because a glyph sitting inside a pill next to text would drop the label onto the next line if it went `block`.',
  'v-glyph-dark': 'The dark half of an icon-badge glyph pair.',
}

/**
 * Reasoning that belongs NEXT TO a specific row rather than in a paragraph
 * someone reads once. Each of these is a defect that has actually shipped.
 */
const CLASS_WARNINGS: Record<string, { title: string; body: string }> = {
  'v-text': {
    title: 'Why `:not(.v-btn)` is in the selector',
    body:
      'The button is an `<a>`, so a plain `.v-text a` — specificity (0,2,0) — BEAT `.v-btn` at (0,1,0). Both rules are `!important`, so the more specific one won: the button kept `.v-btn`’s white background and took `.v-text`’s white text, and the label vanished. The exclusion states the intent — body links follow the text colour, the button does not — and cannot be re-broken by someone reordering the block.',
  },
  'v-btn': {
    title: 'Both halves, always',
    body:
      'A rule that repaints only the background leaves the label behind. That is the single most-repeated defect in this file’s history, which is why `.v-btn`, `.v-avatar` and every `.v-pill-*` each set fill AND ink together rather than relying on a second rule to catch up.',
  },
  'v-avatar': {
    title: 'One class, one answer',
    body:
      'The element carries only `v-avatar`, never `v-muted` as well. Both rules are `!important` at specificity (0,1,0), so a second matching class would be resolved by source order alone — which is precisely how `.v-text` once beat `.v-btn` and painted a button label out of existence.',
  },
  'v-pill-neutral': {
    title: 'The pill selectors are written out literally, and must stay that way',
    body:
      'They are not generated from `PILL_DARK`’s keys in a loop. `apps/api/tests/unit/email-class-coverage.guard.spec.ts` reads this block as SOURCE TEXT and collects `.v-*` names with a regex, so a selector built by interpolating the tone name would define the class at runtime while being invisible to the guard — every pill would then read as “uses a class the dark block does not define”. The VALUES interpolate fine; the SELECTORS must be literal.',
  },
}

/** The shared elements a template composes with. Layer 1 for email (§47). */
const ELEMENTS: { name: string; purpose: string; note?: string }[] = [
  { name: 'EmailHeading', purpose: 'The title sentence. One per email, at the top.' },
  { name: 'EmailText', purpose: 'A body paragraph. `muted` for secondary copy.' },
  { name: 'EmailCode', purpose: 'A large, selectable OTP / verification code. No clipboard JS exists in mail, so it is made maximally selectable instead.' },
  { name: 'EmailCodeBlock', purpose: 'A monospace block for a snippet rather than a code to read out.' },
  { name: 'EmailButton', purpose: 'The single call to action. An `<a>`, styled as a button.' },
  { name: 'EmailDivider', purpose: 'A rule between blocks.' },
  { name: 'EmailQuote', purpose: 'Quoted or user-supplied text, set apart from the message.' },
  { name: 'EmailKeyValueTable', purpose: 'Label/value pairs as a table — order details, summaries.' },
  { name: 'EmailProductCard', purpose: 'A product photo, title and SKU. The photo is pinned to 280×280 by ATTRIBUTES (see the rules tab).' },
  { name: 'EmailActor', purpose: 'The “who” row — avatar plus name, above the thing they acted on.' },
  { name: 'EmailDetailRow', purpose: 'A tight label/value line. 0px between rows, where stacked `EmailText` gives 18px.' },
  {
    name: 'EmailStatusPill',
    purpose: 'A status chip in one of four tones, placed ABOVE a product card.',
    note:
      'Built, but no registered template uses it yet — verified by grep across `packages/emails/src/templates/`. So if `.v-pill-*` is missing from the contract table, that is not a parser fault: the table reports the bytes the API is actually serving, and a class reaches them only once the API is running a `@repo/emails` build that defines it AND a template renders an element carrying it.',
  },
  {
    name: 'EmailIconBadge',
    purpose: 'A glyph in a neutral plate, for a message whose subject is an event rather than an object.',
    note: 'Same status as `EmailStatusPill` — built, not yet used by any registered template.',
  },
]

const STRUCTURAL_RULES: { rule: string; why: string }[] = [
  {
    rule: 'Title sentence → description → the information. Every email, in that order.',
    why: '`transfer-cancelled` shipped with none of it and read as an outlier the moment it landed next to anything else. The order is what makes a stack of unrelated templates feel like one product rather than a dozen authors.',
  },
  {
    rule: 'A status pill goes ABOVE the product card. Never overlaid on it.',
    why: 'An overlay has to survive a photo of unknown colour behind it, in a client that may not load the photo at all. Above the card it is legible in both cases, and it reads in the order it happened: this is the status, this is the item.',
  },
  {
    rule: 'Product images are 280×280, pinned by the `width` and `height` ATTRIBUTES — not by CSS.',
    why: 'Outlook renders through the Word engine, which ignores every `max-*` property and has no `aspect-ratio` at all. A CSS-sized image is unconstrained there, so it renders at its natural pixel size and blows the layout apart. The attributes are the only sizing that survives.',
  },
  {
    rule: 'Light and dark are structurally identical. Colour is the only difference.',
    why: 'There is one document. The light values are inline and the dark values are a `@media` override on top of them, so there is no second layout to keep in step — and no way for one mode to grow an element the other lacks. Prove it in the Live specimen tab: the two panes are the same bytes, rendered twice.',
  },
  {
    rule: '“Powered by” is gated on `hidePoweredBy` AND on a brand logo being present.',
    why: 'Both halves matter. Without the second, a company with white-labelling off but no mark of its own gets Vera’s wordmark presented as if it were theirs, plus a “Powered by” row underneath it — the platform mark attributed to the brand, twice.',
  },
  {
    rule: 'Contrast is enforced at render time, on the FOREGROUND only.',
    why: 'A brand’s background is its own; moving it would be overriding the theme they chose. Moving the ink on top of it is the smallest correction that makes the pair readable. When an adjustment happens the render emits a `<meta name="x-vera-contrast">` record, so the override is greppable in the shipped bytes — and its ABSENCE is the proof a brand rendered untouched. The panel below reads that meta out of the current render.',
  },
]

const TRAPS: { trap: string; why: string }[] = [
  {
    trap: 'No inline SVG. Ever.',
    why: 'Outlook’s Word engine does not render it and Gmail’s sanitiser strips it. The element does not degrade — it disappears, leaving a gap where the glyph was, with nothing in the HTML to suggest anything is missing. Ship a PNG pair and swap it with the `v-glyph-*` classes.',
  },
  {
    trap: 'No `object-fit`.',
    why: 'Unsupported in Outlook 2007–2019, so a non-square image forced to a square box is STRETCHED rather than cropped. Faces distort. Where a crop is genuinely needed, the image has to arrive already cropped.',
  },
  {
    trap: 'Absolute px, never `theme.spacing()`.',
    why: 'A shared component renders under whichever app’s theme mounts it, and the two apps have different spacing bases — `@repo/ui` is 4px, `apps/app` never overrides MUI’s 8px. So the same multiplier renders at double size in one of them, and inside a shared component there is no “convert per app” available (§29, §47). Email has no theme at all, which makes it the same trap with none of the warning signs.',
  },
  {
    trap: 'A colour-bearing element with no `v-*` class.',
    why: 'It renders near-black on a dark card and NOTHING tells you. This is the one that shipped: `EmailCode` carried `v-text` and nothing else, so its text flipped to near-white in dark mode while its `backgroundColor` — an inline literal with no `.v-bg` to override it — stayed the light page tone. Measured at 1.05:1. The guard `email-class-coverage.guard.spec.ts` now derives this from the JSX rather than trusting anyone to remember.',
  },
]

// ── Parsing ──────────────────────────────────────────────────────────────────

/**
 * Read the dark block's rules through the CSSOM rather than a regex.
 *
 * The style element is re-hosted in the REAL document, carrying `media="not
 * all"`, because a `<style>` inside a `DOMParser` document is not CSS-connected
 * — it has no `.sheet`, so `cssRules` is unreachable from there. `not all` never
 * matches, so the rules are parsed and can never paint, which matters given
 * every one of them is `!important`. The node is removed in a `finally`, so a
 * throw mid-parse cannot leave it behind.
 */
const readRules = (css: string): DarkRule[] => {
  const styleEl = document.createElement('style')
  styleEl.setAttribute('media', 'not all')
  styleEl.textContent = css
  document.head.appendChild(styleEl)
  try {
    const sheet = styleEl.sheet
    if (!sheet) return []
    const out: DarkRule[] = []

    const visit = (list: CSSRuleList) => {
      Array.from(list).forEach(rule => {
        // The endpoint unwraps the media query on `mode=dark`, so these arrive
        // top-level. Descending into a surviving `@media` anyway means a change
        // to `forceColorScheme` degrades to "still works" instead of "empty".
        if (typeof CSSMediaRule !== 'undefined' && rule instanceof CSSMediaRule) {
          visit(rule.cssRules)
          return
        }
        if (!(rule instanceof CSSStyleRule)) return
        const classNames = Array.from(
          new Set((rule.selectorText.match(/\.v-[a-z0-9-]+/gi) ?? []).map(s => s.slice(1))),
        )
        if (!classNames.length) return
        const decls: DarkDeclaration[] = []
        for (let i = 0; i < rule.style.length; i += 1) {
          const prop = rule.style[i]
          decls.push({
            prop,
            value: rule.style.getPropertyValue(prop),
            important: rule.style.getPropertyPriority(prop) === 'important',
          })
        }
        out.push({ selectorText: rule.selectorText, classNames, decls })
      })
    }

    visit(sheet.cssRules)
    return out
  } finally {
    styleEl.remove()
  }
}

const parseRender = (darkHtml: string, lightHtml: string): ParsedRender => {
  const parser = new DOMParser()
  const darkDoc = parser.parseFromString(darkHtml, 'text/html')
  const lightDoc = parser.parseFromString(lightHtml, 'text/html')

  // The document carries more than one <style>; the contract is whichever one
  // defines the classes. Identified by content, not by position.
  const block = Array.from(darkDoc.querySelectorAll('style')).find(s =>
    (s.textContent ?? '').includes('.v-'),
  )
  const rules = block ? readRules(block.textContent ?? '') : []

  const definedClasses = Array.from(new Set(rules.flatMap(r => r.classNames)))

  const usedClasses = new Set<string>()
  lightDoc.querySelectorAll('[class]').forEach(el => {
    el.classList.forEach(c => {
      if (c.startsWith('v-')) usedClasses.add(c)
    })
  })

  const contrastMeta =
    lightDoc.querySelector('meta[name="x-vera-contrast"]')?.getAttribute('content') ?? null

  const lightValue = (className: string, prop: string): string | null => {
    let el: Element | null = null
    try {
      el = lightDoc.querySelector(`.${CSS.escape(className)}`)
    } catch {
      return null
    }
    if (!el) return null
    const value = (el as HTMLElement).style?.getPropertyValue(prop)
    return value || null
  }

  return { rules, definedClasses, usedClasses: Array.from(usedClasses), contrastMeta, lightValue }
}

// ── Small presentational pieces ──────────────────────────────────────────────

/** A colour chip beside its literal value. `null` renders as an explicit absence. */
const Swatch = ({ value, prop }: { value: string | null; prop: string }) => {
  const theme = useTheme()
  const isColour = /^(#|rgb|hsl)/i.test(value ?? '')
  if (!value) {
    return (
      <Typography variant="body3" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
        not in this render
      </Typography>
    )
  }
  return (
    <Stack direction="row" spacing={2} alignItems="center">
      {isColour ? (
        <Box
          sx={{
            width: 16,
            height: 16,
            flexShrink: 0,
            borderRadius: '4px',
            bgcolor: value,
            border: `1px solid ${theme.palette.divider}`,
          }}
        />
      ) : null}
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="body3"
          sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', wordBreak: 'break-all' }}
        >
          {value}
        </Typography>
        <Typography variant="body3" sx={{ display: 'block', color: 'text.secondary', fontSize: 11 }}>
          {prop}
        </Typography>
      </Box>
    </Stack>
  )
}

/** An inline callout for reasoning that belongs beside the thing it explains. */
const Note = ({ title, body, tone = 'info' }: { title: string; body: string; tone?: 'info' | 'warn' }) => {
  const theme = useTheme()
  const accent = tone === 'warn' ? theme.palette.warning.main : theme.palette.text.secondary
  return (
    <Box
      sx={{
        mt: 3,
        p: 3,
        borderRadius: 2,
        bgcolor: alpha(accent, 0.06),
        border: `1px solid ${alpha(accent, 0.24)}`,
      }}
    >
      <Typography variant="body3" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
        {title}
      </Typography>
      <Typography variant="body3" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
        {body}
      </Typography>
    </Box>
  )
}

const SectionTitle = ({ children, sub }: { children: React.ReactNode; sub?: string }) => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h6" sx={{ mb: sub ? 1 : 0 }}>
      {children}
    </Typography>
    {sub ? (
      <Typography variant="body3" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
        {sub}
      </Typography>
    ) : null}
  </Box>
)

/**
 * Loading state mirrors the loaded layout region for region (§19) — the picker
 * row, then the table's rows at their real height. A single centred block would
 * shift the whole page when the render arrives.
 */
const ContractSkeleton = () => (
  <Stack spacing={4}>
    <Skeleton height={49} />
    <Card>
      <Stack spacing={3} sx={{ p: 4 }}>
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} height={56} />
        ))}
      </Stack>
    </Card>
  </Stack>
)

// ── Panels ───────────────────────────────────────────────────────────────────

const ContractPanel = ({ parsed }: { parsed: ParsedRender }) => {
  const theme = useTheme()
  const undocumented = parsed.definedClasses.filter(c => !CLASS_NOTES[c])

  return (
    <Stack spacing={5}>
      <Box>
        <SectionTitle sub="Parsed out of the rendered document below, through the CSSOM. Nothing on this page is typed out — a class added to the dark block appears here on its own, and one removed disappears.">
          The `v-*` class contract
        </SectionTitle>

        {undocumented.length ? (
          <Note
            tone="warn"
            title={`${undocumented.length} class${undocumented.length === 1 ? '' : 'es'} with no description in Labs`}
            body={`${undocumented.join(', ')} — the dark block defines these and this surface has nothing to say about them. Add an entry to CLASS_NOTES in EmailStructureLab.tsx. The row still renders; it is the description that is missing, not the class.`}
          />
        ) : null}
      </Box>

      <Card sx={{ p: 0, overflow: 'hidden' }}>
        {parsed.rules.map((rule, ruleIndex) => {
          const primary = rule.classNames[0]
          const note = CLASS_NOTES[primary]
          const warning = CLASS_WARNINGS[primary]
          const unused = rule.classNames.every(c => !parsed.usedClasses.includes(c))

          return (
            <Box
              key={rule.selectorText}
              sx={{
                p: 4,
                borderTop: ruleIndex === 0 ? 'none' : `1px solid ${theme.palette.divider}`,
              }}
            >
              <Stack
                direction="row"
                spacing={4}
                alignItems="flex-start"
                sx={{ flexWrap: 'wrap', rowGap: 3 }}
              >
                <Box sx={{ flex: '1 1 280px', minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontWeight: 600 }}
                  >
                    {rule.selectorText}
                  </Typography>
                  <Stack direction="row" spacing={2} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}>
                    {unused ? (
                      <Typography variant="body3" sx={{ color: 'text.disabled' }}>
                        not used by this render
                      </Typography>
                    ) : null}
                    {rule.decls.some(d => !d.important) ? (
                      <Typography variant="body3" sx={{ color: theme.palette.warning.main }}>
                        a declaration here is not `!important` — it cannot beat an inline style
                      </Typography>
                    ) : null}
                  </Stack>
                  {note ? (
                    <Typography
                      variant="body3"
                      sx={{ display: 'block', mt: 2, color: 'text.secondary', lineHeight: 1.5 }}
                    >
                      {note}
                    </Typography>
                  ) : (
                    <Typography
                      variant="body3"
                      sx={{ display: 'block', mt: 2, color: theme.palette.warning.main, lineHeight: 1.5 }}
                    >
                      No description in Labs for `{primary}` — add one to CLASS_NOTES.
                    </Typography>
                  )}
                </Box>

                <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
                  <Typography
                    variant="body3"
                    sx={{ display: 'block', mb: 2, color: 'text.secondary', fontWeight: 600 }}
                  >
                    Light
                  </Typography>
                  <Stack spacing={2}>
                    {rule.decls.map(d => (
                      <Swatch key={d.prop} prop={d.prop} value={parsed.lightValue(primary, d.prop)} />
                    ))}
                  </Stack>
                </Box>

                <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
                  <Typography
                    variant="body3"
                    sx={{ display: 'block', mb: 2, color: 'text.secondary', fontWeight: 600 }}
                  >
                    Dark
                  </Typography>
                  <Stack spacing={2}>
                    {rule.decls.map(d => (
                      <Swatch key={d.prop} prop={d.prop} value={d.value} />
                    ))}
                  </Stack>
                </Box>
              </Stack>

              {warning ? <Note title={warning.title} body={warning.body} tone="warn" /> : null}
            </Box>
          )
        })}
      </Card>
    </Stack>
  )
}

/**
 * The live coverage check — the same question
 * `email-class-coverage.guard.spec.ts` asks, asked of a rendered document
 * instead of the JSX, and visible rather than only failing a build.
 *
 * The two directions are NOT symmetric and are labelled accordingly. Used but
 * undefined is a DEFECT: that element sets a colour no rule can reach, so it
 * does not flip. Defined but unused is information: the class is available and
 * this particular template has no element that wants it.
 */
const CoveragePanel = ({ parsed }: { parsed: ParsedRender }) => {
  const theme = useTheme()
  const undefinedButUsed = parsed.usedClasses.filter(c => !parsed.definedClasses.includes(c))
  const definedButUnused = parsed.definedClasses.filter(c => !parsed.usedClasses.includes(c))

  return (
    <Card sx={{ p: 4 }}>
      <SectionTitle sub="Compares the classes this render’s elements carry against the classes the dark block defines. Both lists come from the same two documents shown above.">
        Class coverage, for this template
      </SectionTitle>

      <Stack direction="row" spacing={4} sx={{ flexWrap: 'wrap', rowGap: 4 }}>
        <Box sx={{ flex: '1 1 280px', minWidth: 0 }}>
          <Typography variant="body3" sx={{ fontWeight: 600, display: 'block', mb: 2 }}>
            Used but not defined
          </Typography>
          {undefinedButUsed.length ? (
            <Typography variant="body3" sx={{ color: theme.palette.error.main, lineHeight: 1.6 }}>
              {undefinedButUsed.join(', ')} — these elements carry a class no rule repaints, so they do
              not flip in dark mode.
            </Typography>
          ) : (
            <Typography variant="body3" sx={{ color: 'text.secondary' }}>
              None. Every class this render uses has a rule behind it.
            </Typography>
          )}
        </Box>

        <Box sx={{ flex: '1 1 280px', minWidth: 0 }}>
          <Typography variant="body3" sx={{ fontWeight: 600, display: 'block', mb: 2 }}>
            Defined but unused here
          </Typography>
          <Typography variant="body3" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
            {definedButUnused.length
              ? `${definedButUnused.join(', ')} — available, but no element in this template wants them. Normal: most templates use a subset.`
              : 'None — this template exercises the whole block.'}
          </Typography>
        </Box>
      </Stack>

      <Note
        title={parsed.contrastMeta ? 'This render adjusted a colour' : 'This render adjusted nothing'}
        body={
          parsed.contrastMeta
            ? `x-vera-contrast: ${parsed.contrastMeta}. Contrast enforcement moved a foreground colour to make a pair readable, and recorded it. The record ships with the email, so the override is greppable in the bytes rather than being an invisible correction.`
            : 'No `x-vera-contrast` meta is present, which is the positive evidence that every token pair in this render already met the threshold and shipped exactly as the brand set it. An override with no trace would be its own defect, so the absence is the proof.'
        }
      />
    </Card>
  )
}

const SpecimenPanel = ({ darkHtml, lightHtml }: { darkHtml: string; lightHtml: string }) => {
  const theme = useTheme()

  const pane = (title: string, html: string, surface: string) => (
    <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
      <Typography variant="body3" sx={{ fontWeight: 600, display: 'block', mb: 2 }}>
        {title}
      </Typography>
      {/*
        Scoped horizontal scroll rather than a squeezed iframe. The email is a
        600px document by construction, and at 1280 two panes side by side are
        narrower than that — scaling would misrepresent the one thing this
        comparison is for, which is that the two are the SAME layout at the same
        size. The scroll belongs visibly to the pane (§74's sanctioned form).
      */}
      <Box
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          overflow: 'auto',
          bgcolor: surface,
        }}
      >
        <Box
          component="iframe"
          title={title}
          srcDoc={html}
          // Emails carry no scripts, so the most restrictive sandbox still
          // renders them exactly. Images and stylesheets are unaffected.
          sandbox=""
          sx={{ width: 620, height: 760, border: 'none', display: 'block' }}
        />
      </Box>
    </Box>
  )

  return (
    <Stack spacing={4}>
      <SectionTitle sub="The same document, rendered twice. Not two templates and not two layouts — the light values are inline and the dark values are a media override on top of them, so there is no second structure that could drift out of step.">
        Light and dark are the same bytes
      </SectionTitle>
      <Stack direction="row" spacing={4} sx={{ flexWrap: 'wrap', rowGap: 4 }}>
        {pane('Light', lightHtml, '#ffffff')}
        {pane('Dark', darkHtml, '#111111')}
      </Stack>
    </Stack>
  )
}

const RulesPanel = () => {
  const theme = useTheme()

  const list = (
    heading: string,
    sub: string,
    items: { label: string; why: string }[],
    tone: 'neutral' | 'warn',
  ) => (
    <Card sx={{ p: 4 }}>
      <SectionTitle sub={sub}>{heading}</SectionTitle>
      <Stack spacing={4}>
        {items.map(item => (
          <Box
            key={item.label}
            sx={{
              pl: 3,
              borderLeft: `2px solid ${
                tone === 'warn' ? alpha(theme.palette.warning.main, 0.5) : theme.palette.divider
              }`,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.5 }}>
              {item.label}
            </Typography>
            <Typography
              variant="body3"
              sx={{ display: 'block', mt: 1, color: 'text.secondary', lineHeight: 1.6 }}
            >
              {item.why}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Card>
  )

  return (
    <Stack spacing={5}>
      {list(
        'Structural rules',
        'How a message is put together, and the reason each one exists. None of these are preferences.',
        STRUCTURAL_RULES.map(r => ({ label: r.rule, why: r.why })),
        'neutral',
      )}

      {list(
        'Traps',
        'Each of these produces a broken email SILENTLY — the render succeeds, the build passes, and the defect is only visible in a client nobody has open.',
        TRAPS.map(t => ({ label: t.trap, why: t.why })),
        'warn',
      )}

      <Card sx={{ p: 4 }}>
        <SectionTitle sub="Layer 1 for email (§47) — extend one of these rather than styling a one-off inside a template. Every colour-bearing element among them carries the `v-*` class for the token it reads; that is what makes the dark block able to reach it.">
          The shared elements
        </SectionTitle>
        <Stack spacing={3}>
          {ELEMENTS.map(el => (
            <Box key={el.name}>
              <Typography
                variant="body2"
                sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontWeight: 600 }}
              >
                {el.name}
              </Typography>
              <Typography
                variant="body3"
                sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.5 }}
              >
                {el.purpose}
              </Typography>
              {el.note ? (
                <Typography
                  variant="body3"
                  sx={{ display: 'block', mt: 1, color: theme.palette.warning.main, lineHeight: 1.5 }}
                >
                  {el.note}
                </Typography>
              ) : null}
            </Box>
          ))}
        </Stack>
      </Card>
    </Stack>
  )
}

// ── Section ──────────────────────────────────────────────────────────────────

const TABS = [
  { label: 'Dark-mode contract', value: 'contract' },
  { label: 'Live specimen', value: 'specimen' },
  { label: 'Rules & traps', value: 'rules' },
]

/**
 * `/internal/labs/email-structure`.
 *
 * Register in `LabsPage.tsx`'s `LABS_SECTIONS` as
 * `{ key: 'email-structure', label: 'Email structure', render: () => <EmailStructureLab /> }`.
 */
export const EmailStructureLab = () => {
  const [tab, setTab] = useState('contract')
  const [templates, setTemplates] = useState<EmailTemplateSummary[]>([])
  const [selectedId, setSelectedId] = useState<string>('')
  const [darkHtml, setDarkHtml] = useState('')
  const [lightHtml, setLightHtml] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // The registry index. Same endpoint the Notifications lab reads, so the two
  // surfaces can never disagree about which templates exist.
  useEffect(() => {
    let active = true
    api
      .get('/internal/labs/emails')
      .then(({ data }) => {
        if (!active) return
        // `{templates: [...]}`, not a bare array — same shape the Notifications
        // lab reads. An `Array.isArray(data)` guard here silently yielded an
        // empty picker and a skeleton that never resolved.
        const list: EmailTemplateSummary[] = data?.templates ?? []
        setTemplates(list)
        // A `live` template first — a draft renders `GenericEmail` placeholder
        // copy, which exercises far less of the block and would make the light
        // column look emptier than it is.
        const preferred = list.find(t => t.status === 'live') ?? list[0]
        setSelectedId(preferred?.id ?? '')
        if (!preferred) setLoading(false)
      })
      .catch(() => {
        if (!active) return
        setError('Could not load the template index.')
        setLoading(false)
      })
    return () => {
      active = false
    }
  }, [])

  // Both modes of the SAME template. `companyId` is deliberately omitted: an
  // unbranded render resolves the default token set, which is the contract's
  // baseline. Pass a company and every value below becomes that brand's.
  useEffect(() => {
    if (!selectedId) return
    let active = true
    setLoading(true)
    setError(null)
    Promise.all([
      api.get(`/internal/labs/emails/${selectedId}/preview`, {
        responseType: 'text',
        params: { mode: 'dark' },
      }),
      api.get(`/internal/labs/emails/${selectedId}/preview`, {
        responseType: 'text',
        params: { mode: 'light' },
      }),
    ])
      .then(([dark, light]) => {
        if (!active) return
        setDarkHtml(typeof dark.data === 'string' ? dark.data : '')
        setLightHtml(typeof light.data === 'string' ? light.data : '')
      })
      .catch(() => active && setError('Could not render this template.'))
      .finally(() => active && setLoading(false))
    return () => {
      active = false
    }
  }, [selectedId])

  const parsed = useMemo(
    () => (darkHtml && lightHtml ? parseRender(darkHtml, lightHtml) : null),
    [darkHtml, lightHtml],
  )

  const options = useMemo(
    () =>
      templates.map(t => ({
        label: `${t.name}${t.status === 'draft' ? ' (draft)' : ''}`,
        value: t.id,
      })),
    [templates],
  )

  return (
    <Stack spacing={5}>
      <Tabs tabs={TABS} value={tab} onChange={setTab} redirect={false} />

      {/*
        Rules & traps is prose and depends on no render, so it renders BEFORE the
        loading gate rather than inside it — and the picker is hidden there,
        because a control that changes nothing on the surface you are looking at
        is worse than no control.
      */}
      {tab === 'rules' ? (
        <RulesPanel />
      ) : (
        <>
          <Box sx={{ maxWidth: 420 }}>
            <SelectField
              label="Render against"
              options={options}
              value={selectedId}
              onChange={value => setSelectedId(value as string)}
              search
              placeholder="Pick a template"
            />
            <Typography
              variant="body3"
              sx={{ display: 'block', mt: 2, color: 'text.secondary', lineHeight: 1.5 }}
            >
              The class list and its dark values come from the shell, so they are the same whichever
              template you pick. The LIGHT column and the coverage panel are per-template: they can only
              report what this render actually puts on the page.
            </Typography>
          </Box>

          {/*
            The empty case is called out rather than left as a skeleton. A skeleton
            that never resolves reads as a slow page, so the one failure mode it hid
            here — an index that came back in a shape this file did not expect —
            looked identical to loading and stayed on screen indefinitely.
          */}
          {loading ? (
            <ContractSkeleton />
          ) : !parsed ? (
            <Note
              tone="warn"
              title="No render to read"
              body={
                error
                  ? `${error} There are no bytes to parse the class contract out of. Rules & traps still works — it depends on no render.`
                  : 'The template index returned nothing to render, so there are no bytes to parse the class contract out of. Rules & traps still works — it depends on no render.'
              }
            />
          ) : (
            <>
              {tab === 'contract' ? (
                <Stack spacing={5}>
                  <ContractPanel parsed={parsed} />
                  <CoveragePanel parsed={parsed} />
                </Stack>
              ) : null}
              {tab === 'specimen' ? <SpecimenPanel darkHtml={darkHtml} lightHtml={lightHtml} /> : null}
            </>
          )}
        </>
      )}
    </Stack>
  )
}
